import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Seo from "@/components/common/Seo";
import ContactBlock from "@/components/common/ContactBlock";
import { CategoryTag } from "@/components/common/Badges";
import { Linkedin } from "lucide-react";

const CATEGORIES = [
  { kind: "official", title: "Official information", desc: "Government, NOTTO and State Authority data — always clearly labelled with its source.", tint: "#EAF3F0" },
  { kind: "patient", title: "Patient experience", desc: "What a patient, donor or family member reported — in their own words and voice.", tint: "#F9EAEB" },
  { kind: "platform", title: "Platform information", desc: "Context and navigation maintained by Transplant Story.", tint: "#F5EDE1" },
];

export default function About() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#contact") {
      setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location]);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title="About & Contact — Transplant Story" description="Transplant Story is a platform to explore transplant information and hear real experiences from patients, donors and families." />
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-ink">About</h1>
      <p className="text-lg sm:text-xl text-neutral-600 mt-6 max-w-2xl leading-relaxed">
        Transplant Story is a platform designed to help people explore transplant information and hear real
        experiences from patients, donors and families.
      </p>
      <p className="text-base text-neutral-500 mt-4 max-w-2xl leading-relaxed">
        The platform does not rank hospitals or doctors, and it does not provide medical advice. Official
        information is always presented separately from patient-reported experiences.
      </p>

      <h2 className="text-xl font-medium text-ink mt-14 mb-5">How we label information</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {CATEGORIES.map((c) => (
          <div key={c.kind} className="rounded-2xl p-6 transition-transform duration-200 hover:-translate-y-0.5" style={{ backgroundColor: c.tint }}>
            <CategoryTag kind={c.kind} />
            <div className="text-lg font-medium text-ink mt-4">{c.title}</div>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>

      {false && (
        <>
      <h2 className="text-xl font-medium text-ink mt-14 mb-5">Meet the Founder</h2>
      <div className="rounded-2xl bg-white border border-neutral-200 p-6 sm:p-10 grid sm:grid-cols-[220px_1fr] gap-8 items-start">
        <img
          src="/assets/founder.jpg"
          alt="Dr. Gaurav Patel, Founder of TransplantStory"
          data-testid="founder-photo"
          className="w-44 h-44 sm:w-52 sm:h-52 object-cover object-top rounded-2xl shadow-sm"
        />
        <div>
          <div className="text-2xl font-medium text-ink">Dr. Gaurav Patel</div>
          <div className="mt-2 space-y-1">
            <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Founder, TransplantStory</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-rust">Director, Transplant & HPB Surgery</div>
          </div>
          <div className="mt-5 space-y-4 text-neutral-600 leading-relaxed">
            <p>
              After years of caring for transplant patients and their families, I saw how much of the transplant
              journey happens beyond the operating room — the uncertainty, waiting, decisions, complications,
              recovery and the questions families often struggle to answer.
            </p>
            <p>TransplantStory was created to give those experiences a place to be heard.</p>
            <p>
              Our aim is simple: to help people explore real transplant journeys, understand the healthcare
              experience from a patient's perspective, and make information easier to access.
            </p>
            <p>
              TransplantStory is an independent platform. We do not rank hospitals or doctors. We simply believe
              that real experiences, shared responsibly, can help the next family navigate their journey with
              greater understanding.
            </p>
          </div>
          <a
            href="https://www.linkedin.com/in/drgauravpatel"
            target="_blank"
            rel="noreferrer"
            data-testid="founder-linkedin"
            className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-ink border border-neutral-300 px-4 py-2 rounded-full hover:border-ink hover:bg-neutral-50 transition-colors"
          >
            <Linkedin className="w-4 h-4" /> LinkedIn
          </a>
        </div>
      </div>
        </>
      )}

      <h2 id="contact" className="text-xl font-medium text-ink mt-14 mb-5 scroll-mt-24">Contact</h2>
      <div className="rounded-2xl p-6 sm:p-8" style={{ backgroundColor: "#F2EBF4" }}>
        <ContactBlock />
      </div>

      <p className="text-xs text-neutral-400 mt-14 leading-relaxed max-w-2xl">
        This platform is not an emergency service, not a medical complaint mechanism, not a clinical diagnosis
        platform, and not a mechanism for determining medical negligence. It presents experiences and information.
      </p>
    </div>
  );
}
